import { ulid } from 'ulid';
import { dispatch } from '../events/dispatcher.js';
import { db } from '../db/db.js';
import type {
  Category,
  ExpirySource,
  Location,
  OrderSource,
  ShoppingReason,
} from '../events/types.js';
import { lookupKnowledge } from './knowledge.js';
import { normalizeName } from './normalize.js';
import { canonicalKey, knowledgeAliasNormalizedSet } from './canonical.js';
import { normalizeUnitPrice, type NormalizedPrice } from './normalize-price.js';

const DEFAULT_GENERIC = {
  category: 'other' as Category,
  location: 'pantry' as Location,
  expiryDays: 30,
  unit: '個',
};

export interface AddShoppingItemInput {
  name: string;
  quantity?: number;
  unit?: string;
  reason?: ShoppingReason;
  source?: OrderSource;
  sourceName?: string;
  expectedArrival?: Date;
  unitPrice?: number;
  packageSize?: number;
  packageContentUnit?: string;
  /** v0.4 D：由 Subscription 自動建議補單時關聯回 sub.id（用於 markReceived 推進 nextDelivery） */
  subscriptionId?: string;
}

export async function addShoppingItem(input: AddShoppingItemInput): Promise<string> {
  const name = input.name.trim();
  if (!name) throw new Error('addShoppingItem: name required');
  const id = ulid();
  await dispatch('shopping_added', {
    shoppingItemId: id,
    name,
    reason: input.reason ?? 'manual',
    addedAt: Date.now(),
    ...(input.quantity !== undefined ? { quantity: input.quantity } : {}),
    ...(input.unit ? { unit: input.unit } : {}),
    ...(input.source ? { source: input.source } : {}),
    ...(input.sourceName?.trim() ? { sourceName: input.sourceName.trim() } : {}),
    ...(input.expectedArrival ? { expectedArrival: input.expectedArrival.getTime() } : {}),
    ...(input.unitPrice !== undefined && input.unitPrice >= 0
      ? { unitPrice: input.unitPrice }
      : {}),
    ...(input.packageSize !== undefined && input.packageSize > 0
      ? { packageSize: input.packageSize }
      : {}),
    ...(input.packageContentUnit?.trim()
      ? { packageContentUnit: input.packageContentUnit.trim() }
      : {}),
    ...(input.subscriptionId ? { subscriptionId: input.subscriptionId } : {}),
  });
  return id;
}

export async function markOrdered(
  shoppingItemId: string,
  opts: {
    source?: OrderSource;
    sourceName?: string;
    expectedArrival?: Date;
    unitPrice?: number;
    packageSize?: number;
    packageContentUnit?: string;
  } = {},
): Promise<void> {
  await dispatch('shopping_status_changed', {
    shoppingItemId,
    newStatus: 'ordered',
    orderedAt: Date.now(),
    ...(opts.source ? { source: opts.source } : {}),
    ...(opts.sourceName?.trim() ? { sourceName: opts.sourceName.trim() } : {}),
    ...(opts.expectedArrival ? { expectedArrival: opts.expectedArrival.getTime() } : {}),
    ...(opts.unitPrice !== undefined && opts.unitPrice >= 0
      ? { unitPrice: opts.unitPrice }
      : {}),
    ...(opts.packageSize !== undefined && opts.packageSize > 0
      ? { packageSize: opts.packageSize }
      : {}),
    ...(opts.packageContentUnit?.trim()
      ? { packageContentUnit: opts.packageContentUnit.trim() }
      : {}),
  });
}

/**
 * v0.4：標記團購/網購已出貨在途中。可從 planned 或 ordered 直接轉入。
 * 與 markOrdered 的差別：in_transit 表示「賣家已寄出 / 已開始送貨」，
 * UI 強調 ETA（expectedArrival）；ordered 純粹是「下單但未出貨」。
 */
export async function markInTransit(
  shoppingItemId: string,
  opts: {
    source?: OrderSource;
    sourceName?: string;
    expectedArrival?: Date;
    unitPrice?: number;
    packageSize?: number;
    packageContentUnit?: string;
  } = {},
): Promise<void> {
  await dispatch('shopping_status_changed', {
    shoppingItemId,
    newStatus: 'in_transit',
    inTransitAt: Date.now(),
    ...(opts.source ? { source: opts.source } : {}),
    ...(opts.sourceName?.trim() ? { sourceName: opts.sourceName.trim() } : {}),
    ...(opts.expectedArrival ? { expectedArrival: opts.expectedArrival.getTime() } : {}),
    ...(opts.unitPrice !== undefined && opts.unitPrice >= 0
      ? { unitPrice: opts.unitPrice }
      : {}),
    ...(opts.packageSize !== undefined && opts.packageSize > 0
      ? { packageSize: opts.packageSize }
      : {}),
    ...(opts.packageContentUnit?.trim()
      ? { packageContentUnit: opts.packageContentUnit.trim() }
      : {}),
  });
}

export async function cancelShoppingItem(shoppingItemId: string): Promise<void> {
  await dispatch('shopping_status_changed', {
    shoppingItemId,
    newStatus: 'cancelled',
  });
}

export interface MarkReceivedOverrides {
  category?: Category;
  location?: Location;
  quantity?: number;
  unit?: string;
  expiresAt?: Date | null;
  expirySource?: ExpirySource;
  unitPrice?: number;
  packageSize?: number;
  packageContentUnit?: string;
  sourceName?: string;
}

/**
 * Mark a shopping item as received and create the corresponding inventory batch
 * (兩階段入庫的入庫端). Defaults pulled from PantryKnowledge → generic fallback.
 */
export async function markReceived(
  shoppingItemId: string,
  overrides: MarkReceivedOverrides = {}
): Promise<{ itemId: string; batchId: string; isNewItem: boolean }> {
  const shopping = await db.shoppingItems.get(shoppingItemId);
  if (!shopping) throw new Error('shopping item not found');

  const normalized = normalizeName(shopping.name);
  const knowledge = await lookupKnowledge(shopping.name);
  // v0.4 alias normalization：跨 PantryKnowledge alias 找既有 active item，
  // 例：採買「鮮乳」進來，庫存裡的「全脂鮮乳」（同 fresh_milk knowledge）會被 reuse。
  const aliasSet = await knowledgeAliasNormalizedSet(shopping.name);
  const candidates = await db.items
    .where('normalizedName')
    .anyOf([...aliasSet])
    .toArray();
  // 同一 canonical 多筆 active 時，挑最早建立（穩定排序）；ulid 字典序即時間序。
  candidates.sort((a, b) => a.id.localeCompare(b.id));
  const existing = candidates[0];

  const category = overrides.category ?? knowledge?.category ?? DEFAULT_GENERIC.category;
  const location = overrides.location ?? knowledge?.defaultLocation ?? DEFAULT_GENERIC.location;
  const packagingUnit =
    overrides.unit ?? shopping.unit ?? knowledge?.defaultUnit ?? DEFAULT_GENERIC.unit;
  const buyQuantity = overrides.quantity ?? shopping.quantity ?? 1;

  // v0.3.4 重設計：batch 永遠用 packaging storage（quantity=瓶/盒/包數）。
  // packageSize / packageUnit 僅為 metadata（每瓶多少 base unit），用於比價 + 拆 batch。
  // 這樣使用者「我有 4 瓶」直覺保留，不會看到 3744ml 這種數字。
  const pkgSize = overrides.packageSize ?? shopping.packageSize;
  const pkgContentUnit = overrides.packageContentUnit ?? shopping.packageContentUnit;
  const trimmedContentUnit = pkgContentUnit?.trim() ?? '';
  const hasPackagingInfo =
    pkgSize !== undefined &&
    pkgSize > 0 &&
    trimmedContentUnit.length > 0 &&
    trimmedContentUnit !== packagingUnit;

  const expiryDays = knowledge?.defaultExpiryDays ?? DEFAULT_GENERIC.expiryDays;
  const expiresAt =
    overrides.expiresAt !== undefined
      ? overrides.expiresAt?.getTime() ?? null
      : Date.now() + expiryDays * 86_400_000;
  const expirySource: ExpirySource =
    overrides.expirySource ??
    (overrides.expiresAt !== undefined
      ? 'manual'
      : knowledge
      ? 'knowledge_base'
      : 'estimated');

  // 對既有同名 item：active / all_consumed / all_expired 都 reuse（projector 會復活 status）；
  // 只有 discarded 才另建新 item。
  const reuseExisting = existing && existing.status !== 'discarded';
  const isNewItem = !reuseExisting;
  const itemId = reuseExisting ? existing!.id : ulid();
  const batchId = ulid();

  await dispatch('shopping_received', {
    shoppingItemId,
    receivedAt: Date.now(),
    isNewItem,
    itemId,
    batchId,
    name: shopping.name,
    normalizedName: normalized,
    category,
    location,
    quantity: buyQuantity,
    unit: packagingUnit,
    ...(hasPackagingInfo
      ? { batchPackageSize: pkgSize, batchPackageUnit: trimmedContentUnit }
      : {}),
    expiresAt,
    expirySource,
    ...(overrides.unitPrice !== undefined && overrides.unitPrice >= 0
      ? { unitPrice: overrides.unitPrice }
      : {}),
    ...(overrides.packageSize !== undefined && overrides.packageSize > 0
      ? { packageSize: overrides.packageSize }
      : {}),
    ...(overrides.packageContentUnit?.trim()
      ? { packageContentUnit: overrides.packageContentUnit.trim() }
      : {}),
    ...(overrides.sourceName?.trim() ? { sourceName: overrides.sourceName.trim() } : {}),
  });

  // v0.4 D：若這筆 ShoppingItem 來自 Subscription，自動推進 nextDelivery。
  // 注意：此處 import 用 dynamic 以免循環 dep（subscription.ts 依賴 db；shopping.ts 也依賴 db）。
  if (shopping.subscriptionId) {
    const { advanceSubscription } = await import('./subscription.js');
    try {
      await advanceSubscription(shopping.subscriptionId, 'received');
    } catch {
      // subscription 可能已被 deactivate / 刪除；不應阻擋 markReceived
    }
  }

  return { itemId, batchId, isNewItem };
}

/**
 * 找最近一次同品項（依 canonical key 對齊）已 received 且帶 unitPrice 的紀錄。
 * 用於採買清單「上次 NT$ X」歷史價格提示 + normalized 比價。
 *
 * v0.4 起：用 canonicalKey（PantryKnowledge.id）對齊，所以「全脂鮮乳」「鮮乳」
 * 「milk」會視為同品項；無 knowledge 對應時 fallback `normalizeName` exact match。
 *
 * 只看 status === 'received' 且 unitPrice 有值的 ShoppingItem。
 *
 * 回傳：除原始 unitPrice 外，若紀錄包含 packageSize + packageContentUnit
 * 則一併回 normalized price（用於跨包裝規格的比價）。
 */
export interface LastUnitPrice {
  unitPrice: number;
  receivedAt: number;
  unit?: string;
  packageSize?: number;
  packageContentUnit?: string;
  sourceName?: string;
  shoppingItemId: string;
  /** v0.2 G-full：normalized「每比價單位」價格，供同品項不同包裝規格比價用。 */
  normalized?: NormalizedPrice;
}

export async function lookupLastUnitPrice(name: string): Promise<LastUnitPrice | undefined> {
  const target = normalizeName(name);
  if (!target) return undefined;
  const targetCanonical = await canonicalKey(name);
  const all = await db.shoppingItems.toArray();
  let best: LastUnitPrice | undefined;
  for (const row of all) {
    if (row.status !== 'received') continue;
    if (row.unitPrice === undefined) continue;
    if (row.receivedAt === undefined) continue;
    const rowCanonical = await canonicalKey(row.name);
    if (rowCanonical !== targetCanonical) continue;
    if (!best || row.receivedAt > best.receivedAt) {
      const normalized = normalizeUnitPrice(
        row.unitPrice,
        row.packageSize,
        row.packageContentUnit,
        row.unit,
      );
      best = {
        unitPrice: row.unitPrice,
        receivedAt: row.receivedAt,
        shoppingItemId: row.id,
        ...(row.unit ? { unit: row.unit } : {}),
        ...(row.packageSize !== undefined ? { packageSize: row.packageSize } : {}),
        ...(row.packageContentUnit
          ? { packageContentUnit: row.packageContentUnit }
          : {}),
        ...(row.sourceName ? { sourceName: row.sourceName } : {}),
        ...(normalized ? { normalized } : {}),
      };
    }
  }
  return best;
}

/**
 * 回傳 db.shoppingItems 中 unique 的 sourceName / unit / packageContentUnit，
 * 用於採買 form 三個 datalist 的 "user history" 自動建議來源。
 *
 * 只看尚未 cancelled 的 row（cancelled 的 sourceName 經常是錯的、不該推薦）。
 */
export interface ShoppingDatalistOptions {
  sourceNames: string[];
  units: string[];
  packageContentUnits: string[];
}

export async function loadShoppingDatalistOptions(): Promise<ShoppingDatalistOptions> {
  const all = await db.shoppingItems.toArray();
  const sourceNames = new Set<string>();
  const units = new Set<string>();
  const packageContentUnits = new Set<string>();
  for (const row of all) {
    if (row.status === 'cancelled') continue;
    if (row.sourceName) sourceNames.add(row.sourceName);
    if (row.unit) units.add(row.unit);
    if (row.packageContentUnit) packageContentUnits.add(row.packageContentUnit);
  }
  return {
    sourceNames: [...sourceNames].sort(),
    units: [...units].sort(),
    packageContentUnits: [...packageContentUnits].sort(),
  };
}
